import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useState } from 'react';
import { getAddress } from 'viem';
import { useDialogStore } from '@/components/dialogs';
import useAuthStore from '../../../store/rootStore';
import { telemetry } from '../../../utils/telemetry';
import { useAuthSignatureMutation } from '../authHooks.query.ts';

export type DialogStep = 'enterAccessCode' | 'accessGranted' | 'restricted';

const getInitialStep = (): DialogStep => {
  const pendingRequest = useAuthStore.getState().pendingAccessRequest;
  if (pendingRequest?.errorStatus === 403) {
    return 'restricted';
  }
  return 'enterAccessCode';
};

export const useAccessRequestState = () => {
  const { t } = useLingui();
  const [accessCode, setAccessCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<DialogStep>(getInitialStep);
  const [error, setError] = useState('');

  const address = useAuthStore((state) => state.address);
  const { mutate: authorizeSignature } = useAuthSignatureMutation();

  useEffect(() => {
    if (!address) {
      setCurrentStep(getInitialStep());
      setAccessCode('');
      setError('');
      setIsLoading(false);
    }
  }, [address]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  const resetToInitial = useCallback(() => {
    setCurrentStep('enterAccessCode');
    setError('');
    setAccessCode('');
  }, []);

  const submitAccessCode = useCallback(async () => {
    if (!accessCode.trim()) {
      setError(t`Please enter an access code`);
      return false;
    }

    const pendingRequest = useAuthStore.getState().pendingAccessRequest;
    if (!pendingRequest?.signedRequest) {
      setError(t`No pending authentication request found`);
      return false;
    }

    setIsLoading(true);
    setError('');

    const requestData = {
      ...pendingRequest.signedRequest,
      ref_code: accessCode.trim(),
    };

    return new Promise<boolean>((resolvePromise) => {
      authorizeSignature(requestData, {
        onSuccess: (data) => {
          const { authenticate, saveInviteCode } = useAuthStore.getState();
          authenticate(data, getAddress(requestData.address), requestData.chain_id);
          saveInviteCode(accessCode.trim());
          telemetry.logOnboarding('authenticated', { via: 'access_code' });
          setCurrentStep('accessGranted');
          setIsLoading(false);
          resolvePromise(true);
        },
        onError: (err: any) => {
          if (err.response?.status === 403) {
            useAuthStore.getState().setRestricted();
            telemetry.logRestrictedAccess(getAddress(requestData.address));
            telemetry.logOnboarding('failed', { step: 'auth', reason: 'restricted' });
            setCurrentStep('restricted');
          } else if (err.response?.status === 451) {
            useAuthStore.getState().blockByAuthority({ step: 'auth' });
            useDialogStore.getState().closeDialog('accessRequestDialog');
            useDialogStore.getState().openDialog('geoBlockDialog');
          } else {
            telemetry.logOnboarding('failed', { step: 'auth', reason: 'access_code_rejected' });
            setError(
              err.response?.data?.error ||
                t`This code is invalid, or there are no more invites currently associated with this code.`
            );
          }
          setIsLoading(false);
          resolvePromise(false);
        },
      });
    });
  }, [accessCode, authorizeSignature, t]);

  return {
    // State
    accessCode,
    setAccessCode,
    isLoading,
    currentStep,
    setCurrentStep,
    error,
    address,
    // Actions
    clearError,
    resetToInitial,
    submitAccessCode,
  };
};
